import React, { FC, useMemo } from "react";
import { SeriesId } from "@mui/x-charts/internals";
import { useLinePoint } from "./useLinePoint";

export interface LineAnnotationProps {
  seriesId: SeriesId | undefined;
  dataIndex: number | undefined;
  label: string;
  color?: string; // applied to shapes (path, rect stroke, circle); text uses theme default
  onClick?: () => void;
}

export const LineAnnotation: FC<LineAnnotationProps> = (props) => {
  const { seriesId, dataIndex, label, color, onClick } = props;

  // Styling and sizing tuned to reduce padding around the label
  const fontSize = 12;
  const padX = 6; // tighter left/right padding
  const padY = 3; // tighter top/bottom padding

  // Rough text width estimate (SVG doesn't measure without layouts). Keep minimal width.
  const estimatedTextWidth = useMemo(() => {
    const avgCharWidth = fontSize * 0.6; // heuristic for typical fonts
    return Math.max(fontSize * 1.5, label.length * avgCharWidth);
  }, [label, fontSize]);

  const data = useLinePoint(seriesId, dataIndex);
  if (data === null) {
    return null;
  }

  const labelWidth = Math.ceil(estimatedTextWidth + padX * 2);
  const labelHeight = Math.ceil(fontSize + padY * 2);

  // Position the label to the north-east of the point with a shorter string
  const dx = 26; // east offset (shorter)
  const dy = 20; // north offset (shorter)

  // Place the label so that its bottom-left corner is the string anchor
  const labelBottomY = data.y - dy;
  const labelX = data.x + dx; // bottom-left corner is east of the point
  const labelY = labelBottomY - labelHeight;

  // Text will be centered within the label
  const textX = labelX + labelWidth / 2;

  // Create a more pronounced, "snake-like" string/path from the point to the label.
  // Use a cubic Bezier with stronger lateral deviation to form an S-curve.
  const x0 = data.x;
  const y0 = data.y;
  const x1 = labelX; // attach to bottom-left corner of label
  const y1 = labelBottomY;

  // Choose drawing color: prefer explicit color prop, then series color, else black
  const shapeColor = color || data.series.color || "black";

  // S-shaped curve: first control far to the right/up, second control left/down near the end
  const cx1 = x0 + dx * 0.9;
  const cy1 = y0 - dy * 0.3;
  const cx2 = x1 - dx * 0.6;
  const cy2 = y1 + dy * 0.5;

  const stringPath = `M ${x0},${y0} C ${cx1},${cy1} ${cx2},${cy2} ${x1},${y1}`;

  return (
    <g>
      {/* Swaying string attaching label to center point */}
      <path d={stringPath} fill="none" stroke={shapeColor} strokeWidth={1.5} />

      {/* Center point marker */}
      <circle cx={data.x} cy={data.y} r={4} fill={shapeColor} />

      {/* Label shape */}
      <rect
        x={labelX}
        y={labelY}
        width={labelWidth}
        height={labelHeight}
        fill="white"
        stroke={shapeColor}
        strokeWidth={1.5}
        rx={3}
        ry={3}
      />

      {/* Label text: use theme default via currentColor */}
      <text
        x={textX}
        y={labelY + labelHeight / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="currentColor"
        fontSize={fontSize}
        fontWeight="500"
        onClick={onClick}
        style={{ cursor: onClick ? "pointer" : undefined }}
      >
        {label}
      </text>
    </g>
  );
};
